import React from "react";
import PropTypes from "prop-types";
import styles from "./Button.module.scss";

/**
 * Button component - A reusable button component with multiple variants and sizes
 * 
 * @param {string} variant - Button variant: 'primary' | 'secondary' | 'link'
 * @param {string} size - Button size: 'small' | 'medium' | 'large'
 * @param {string} borderRadius - Border radius: 'round' | 'square'
 * @param {boolean} fullWidth - Whether button should take full width
 * @param {boolean} disabled - Whether button is disabled
 * @param {string} className - Additional CSS classes
 * @param {string} type - Button type: 'button' | 'submit' | 'reset'
 * @param {function} onClick - Click handler
 * @param {React.ReactNode} children - Button content
 * @param {object} ...rest - Any other button props (data-testid, etc.)
 */
const Button = ({
  variant = "primary",
  size = "medium",
  borderRadius = "round",
  fullWidth = false,
  disabled = false,
  error = false,
  className = "",
  type = "button",
  onClick,
  children,
  ...rest
}) => {
  const buttonClasses = [
    styles.button,
    styles[variant],
    styles[size],
    styles[`border-${borderRadius}`],
    fullWidth && styles.fullWidth,
    disabled && styles.disabled,
    error && styles.error,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
      {...rest}
    >
      {children}
    </button>
  );
};

Button.propTypes = {
  variant: PropTypes.oneOf(["primary", "secondary", "link"]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  borderRadius: PropTypes.oneOf(["round", "square"]),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
};

export default Button;

